import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  Upload as UploadIcon,
  CloudUpload,
  CheckCircle,
  ShoppingCart,
} from "@mui/icons-material";
import { clientService, Client } from "../../services/client.service";
import apiClient from "../../services/api";
import { Layout } from "../Layout";
import { useNavigate } from "react-router-dom";

interface PatientData {
  memberId: string;
  firstname: string;
  lastname: string;
  dob: string;
  phone: string;
  email: string;
  glTestRate: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  notes?: string;
  existsInTNS: boolean;
  hasClientAssociation?: boolean;
  tnsPatientId?: number;
  tnsAccountId?: number;
  matchMethod?: string;
  matchConfidence?: "high" | "medium" | "low";
}

interface Programs {
  diabetes: boolean;
  weight: boolean;
  hypertension: boolean;
}

interface CCMCareProvider {
  _id: string;
  fname: string;
  lname: string;
  email?: string;
  username?: string;
}

interface PatientResult {
  patient: PatientData;
  tnsSuccess: boolean;
  tnsError?: string;
  tnsWasExisting?: boolean;
  partnerSuccess: boolean;
  partnerError?: string;
  partnerWasExisting?: boolean;
}

const PatientImport: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<number>(0);
  const [sheets, setSheets] = useState<Record<string, any[]>>({});
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [patients, setPatients] = useState<PatientData[]>([]);
  const [selectedPatients, setSelectedPatients] = useState<Set<number>>(
    new Set(),
  );
  const [selectedProgram, setSelectedProgram] = useState<string>("none");

  // Convert selected program to Programs object for backend
  const programs: Programs = {
    diabetes: selectedProgram === "diabetes",
    weight: selectedProgram === "weight",
    hypertension: selectedProgram === "hypertension",
  };

  // Care provider state
  const [careProviders, setCareProviders] = useState<CCMCareProvider[]>([]);
  const [selectedCareProvider, setSelectedCareProvider] =
    useState<CCMCareProvider | null>(null);
  const [loadingCareProviders, setLoadingCareProviders] = useState(false);

  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [generatingPO, setGeneratingPO] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<PatientResult[]>([]);
  const [deactivationResults, setDeactivationResults] = useState<
    {
      patientName: string;
      memberId: string;
      tnsSuccess: boolean;
      tnsAlreadyDeactivated: boolean;
      ccmSuccess: boolean | null;
      ccmError?: string;
    }[]
  >([]);
  const [dragActive, setDragActive] = useState(false);

  // PO selection states
  const [poOption, setPOOption] = useState<"new" | "existing">("new");
  const [matchingPOs, setMatchingPOs] = useState<any[]>([]);
  const [selectedPO, setSelectedPO] = useState<number>(0);
  const [loadingPOs, setLoadingPOs] = useState(false);

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  // Load care providers when program is selected for CCM-enabled entities
  useEffect(() => {
    if (selectedProgram !== "none" && selectedClient && patients.length > 0) {
      // Only load care providers for CCM-enabled entities (PHM, WPH, VHC)
      const ccmEnabledEntities = [1008, 983, 2016];
      if (ccmEnabledEntities.includes(selectedClient)) {
        loadCareProviders(selectedClient);
      }
    }
  }, [selectedProgram, selectedClient, patients.length]);

  const loadClients = async () => {
    try {
      const response = await apiClient.get("/import/clients");
      if (response.data.success) {
        setClients(response.data.clients);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load clients");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const processFile = async (file: File) => {
    if (!selectedClient) {
      setError("Please select a client first");
      return;
    }

    setSelectedFile(file);
    setSheets({});
    setSheetNames([]);
    setSelectedSheet("");
    setPatients([]);
    setSelectedPatients(new Set());
    setError(null);
    setImportResults([]);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post("/import/parse", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setSheets(response.data.sheets);
        setSheetNames(response.data.sheetNames);
        if (response.data.sheetNames.length > 0) {
          setSelectedSheet(response.data.sheetNames[0]);
          // Auto-load first sheet
          const sheetData = response.data.sheets[response.data.sheetNames[0]];
          if (sheetData && sheetData.length > 0) {
            if (!selectedClient) {
              setError("Please select a client before processing");
              setLoading(false);
              return;
            }
            const validateResponse = await apiClient.post("/import/validate", {
              patients: sheetData,
              entityId: selectedClient,
            });
            if (validateResponse.data.success) {
              setPatients(validateResponse.data.patients);
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to parse file");
    } finally {
      setLoading(false);
    }
  };

  const handleSheetChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const sheetName = event.target.value;
    setSelectedSheet(sheetName);
    setLoading(true);
    setError(null);
    setDeactivationResults([]);
    setImportResults([]);

    try {
      const sheetData = sheets[sheetName];
      if (sheetData && sheetData.length > 0) {
        if (!selectedClient) {
          setError("Please select a client before loading sheets");
          setLoading(false);
          return;
        }
        // Validate patients
        const response = await apiClient.post("/import/validate", {
          patients: sheetData,
          entityId: selectedClient,
        });

        if (response.data.success) {
          console.log(
            "Validated patients with match data:",
            response.data.patients,
          );
          setPatients(response.data.patients);
          setSelectedPatients(new Set());
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to validate patients");
    } finally {
      setLoading(false);
    }
  };

  const handlePatientToggle = (index: number) => {
    const newSelected = new Set(selectedPatients);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedPatients(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPatients.size === patients.length) {
      setSelectedPatients(new Set());
    } else {
      setSelectedPatients(new Set(patients.map((_, idx) => idx)));
    }
  };

  const loadCareProviders = async (entityId: number) => {
    setLoadingCareProviders(true);
    try {
      const response = await apiClient.get(
        `/patients/ccm/careproviders/${entityId}`,
      );
      if (response.data.careProviders) {
        setCareProviders(response.data.careProviders);
        // Auto-select first care provider if available
        if (response.data.careProviders.length > 0) {
          setSelectedCareProvider(response.data.careProviders[0]);
        }
      }
    } catch (err: any) {
      console.error("Failed to load care providers:", err);
      setError(
        "Failed to load care providers. You can still proceed with default assignment.",
      );
    } finally {
      setLoadingCareProviders(false);
    }
  };

  const handleImport = async () => {
    if (selectedPatients.size === 0) {
      setError("Please select at least one patient to import");
      return;
    }

    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    setImporting(true);
    setError(null);
    setImportResults([]);

    try {
      const selectedPatientData = Array.from(selectedPatients).map(
        (idx) => patients[idx],
      );

      const response = await apiClient.post("/import/import", {
        patients: selectedPatientData,
        entityId: selectedClient,
        programs,
        careProviderId: selectedCareProvider?._id,
      });

      if (response.data.success) {
        setImportResults(response.data.results);
        // Clear selection
        setSelectedPatients(new Set());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to import patients");
    } finally {
      setImporting(false);
    }
  };

  const handleDeactivate = async () => {
    if (selectedPatients.size === 0) {
      setError("Please select at least one patient to deactivate");
      return;
    }

    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    setImporting(true);
    setError(null);
    setDeactivationResults([]);

    try {
      const selectedPatientData = Array.from(selectedPatients).map(
        (idx) => patients[idx],
      );

      const patientIds = selectedPatientData
        .map((p) => p.tnsPatientId)
        .filter((id) => id !== undefined);

      const response = await apiClient.post("/import/deactivate", {
        patientIds,
        clientId: selectedClient,
      });

      if (response.data.success) {
        // Map API per-patient results back to display data
        const apiResultsById = new Map<
          number,
          {
            tnsSuccess: boolean;
            tnsAlreadyDeactivated: boolean;
            ccmSuccess: boolean | null;
            ccmError?: string;
          }
        >((response.data.results ?? []).map((r: any) => [r.patientId, r]));
        const results = selectedPatientData.map((p) => {
          const apiResult =
            p.tnsPatientId !== undefined
              ? apiResultsById.get(p.tnsPatientId)
              : undefined;
          return {
            patientName: `${p.firstname} ${p.lastname}`,
            memberId: p.memberId,
            tnsSuccess: apiResult?.tnsSuccess ?? true,
            tnsAlreadyDeactivated: apiResult?.tnsAlreadyDeactivated ?? false,
            ccmSuccess: apiResult?.ccmSuccess ?? null,
            ccmError: apiResult?.ccmError,
          };
        });
        setDeactivationResults(results);
        // Clear selection
        setSelectedPatients(new Set());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to deactivate patients");
    } finally {
      setImporting(false);
    }
  };

  const handleGeneratePO = async () => {
    setGeneratingPO(true);
    setError(null);

    try {
      // Collect patient IDs and external IDs from successful imports
      const patientData = importResults
        .filter((r) => r.tnsSuccess)
        .map((r) => ({
          patientID: r.patient.tnsPatientId,
          externalID: r.patient.memberId,
        }));

      const response = await apiClient.post(
        "/purchase-orders/generate-from-import",
        {
          clientID: selectedClient,
          programs,
          patients: patientData,
        },
      );

      if (response.data.success) {
        // Navigate to the created PO's edit page
        navigate(`/purchase-orders/${response.data.purchaseOrderId}/edit`);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to generate purchase order",
      );
    } finally {
      setGeneratingPO(false);
    }
  };

  const loadMatchingPOs = async () => {
    setLoadingPOs(true);
    setError(null);

    try {
      const params: any = { clientID: selectedClient };
      // Add subClientId if one was selected (future enhancement)

      const response = await apiClient.get(
        "/purchase-orders/matching-for-import",
        {
          params,
        },
      );

      if (response.data.success) {
        setMatchingPOs(response.data.purchaseOrders);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to load matching purchase orders",
      );
    } finally {
      setLoadingPOs(false);
    }
  };

  const handleAddToExistingPO = async () => {
    if (!selectedPO) {
      setError("Please select a purchase order");
      return;
    }

    setGeneratingPO(true);
    setError(null);

    try {
      const patientData = importResults
        .filter((r) => r.tnsSuccess)
        .map((r) => ({
          patientID: r.patient.tnsPatientId,
          externalID: r.patient.memberId,
        }));

      const response = await apiClient.post(
        "/purchase-orders/add-to-existing",
        {
          purchaseOrderID: selectedPO,
          programs,
          patients: patientData,
        },
      );

      if (response.data.success) {
        // Navigate to the updated PO's edit page
        navigate(`/purchase-orders/${selectedPO}/edit`);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to add patients to purchase order",
      );
    } finally {
      setGeneratingPO(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant='h4' gutterBottom>
          Patient Enrollment Import
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Client Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              1. Select Client
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Choose which partner organization you are processing enrollments
              for.
            </Typography>
            <Select
              value={selectedClient}
              onChange={(e) => {
                setSelectedClient(Number(e.target.value));
                // Reset file state when client changes
                setSelectedFile(null);
                setSheets({});
                setSheetNames([]);
                setPatients([]);
                setImportResults([]);
              }}
              fullWidth
              displayEmpty
            >
              <MenuItem value={0}>Select a client...</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.companyName} ({client.id})
                </MenuItem>
              ))}
            </Select>
          </CardContent>
        </Card>

        {/* Step 2: File Upload */}
        {selectedClient > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                2. Upload Enrollment File
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Drop an XLSX file here or click to select.
              </Typography>

              <Box
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                sx={{
                  border: dragActive ? "2px dashed #1976d2" : "2px dashed #ccc",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  bgcolor: dragActive ? "action.hover" : "background.paper",
                  cursor: "pointer",
                  transition: "all 0.3s",
                }}
              >
                {loading ? (
                  <Box>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography>Processing file...</Typography>
                  </Box>
                ) : selectedFile ? (
                  <Box>
                    <Typography variant='body1' sx={{ mb: 1 }}>
                      ✓ {selectedFile.name}
                    </Typography>
                    <Button variant='outlined' component='label' size='small'>
                      Change File
                      <input
                        type='file'
                        hidden
                        accept='.xlsx,.xls'
                        onChange={handleFileSelect}
                      />
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <CloudUpload
                      sx={{ fontSize: 48, color: "action.active", mb: 2 }}
                    />
                    <Typography variant='body1' sx={{ mb: 1 }}>
                      Drag and drop an XLSX file here
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 2 }}
                    >
                      or
                    </Typography>
                    <Button
                      variant='contained'
                      component='label'
                      startIcon={<UploadIcon />}
                    >
                      Select File
                      <input
                        type='file'
                        hidden
                        accept='.xlsx,.xls'
                        onChange={handleFileSelect}
                      />
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Tab Selection */}
        {sheetNames.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                3. Select Tab to Process
              </Typography>
              <Tabs
                value={selectedSheet}
                onChange={(_, newValue) => {
                  const event = {
                    target: { value: newValue },
                  } as React.ChangeEvent<HTMLInputElement>;
                  handleSheetChange(event);
                }}
                variant='scrollable'
                scrollButtons='auto'
              >
                {sheetNames.map((name) => (
                  <Tab
                    key={name}
                    value={name}
                    label={`${name} (${sheets[name]?.length || 0})`}
                  />
                ))}
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Program Selection */}
        {patients.length > 0 &&
          selectedSheet.toLowerCase() !== "deactivate" && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  4. Select Program
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 2 }}
                >
                  Choose which program these patients are enrolling in.
                </Typography>
                <Select
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  fullWidth
                >
                  <MenuItem value='none'>Select a program...</MenuItem>
                  <MenuItem value='diabetes'>Diabetes</MenuItem>
                  <MenuItem value='weight'>Weight Management</MenuItem>
                  <MenuItem value='hypertension'>Hypertension</MenuItem>
                </Select>
              </CardContent>
            </Card>
          )}

        {/* Step 5: Care Provider Selection (for CCM-enabled entities) */}
        {patients.length > 0 &&
          selectedSheet.toLowerCase() !== "deactivate" &&
          selectedProgram !== "none" &&
          careProviders.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  5. Select Care Provider
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 2 }}
                >
                  Choose which care provider to assign to these patients in the
                  CCM system. If not selected, the default care provider will be
                  used.
                </Typography>
                {loadingCareProviders ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Loading care providers...</Typography>
                  </Box>
                ) : (
                  <Autocomplete
                    options={careProviders}
                    getOptionLabel={(option) =>
                      `${option.fname} ${option.lname}${option.email ? ` (${option.email})` : ""}`
                    }
                    value={selectedCareProvider}
                    onChange={(event, newValue) =>
                      setSelectedCareProvider(newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Care Provider'
                        placeholder='Search by name or email...'
                      />
                    )}
                    fullWidth
                  />
                )}
              </CardContent>
            </Card>
          )}

        {/* Step 6: Patient Selection */}
        {patients.length > 0 && !loading && (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant='h6'>
                    6. Select Patients to Process ({selectedPatients.size}{" "}
                    selected)
                  </Typography>
                  <Button variant='outlined' onClick={handleSelectAll}>
                    {selectedPatients.size === patients.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </Box>

                <TableContainer component={Paper}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={
                              selectedPatients.size === patients.length &&
                              patients.length > 0
                            }
                            indeterminate={
                              selectedPatients.size > 0 &&
                              selectedPatients.size < patients.length
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>ID</TableCell>
                        <TableCell>External ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>DOB</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Test Rate/Count</TableCell>
                        <TableCell align='center'>Client</TableCell>
                        <TableCell align='center'>TNS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patients.map((patient, idx) => (
                        <React.Fragment key={idx}>
                          <TableRow>
                            <TableCell padding='checkbox'>
                              <Checkbox
                                checked={selectedPatients.has(idx)}
                                onChange={() => handlePatientToggle(idx)}
                              />
                            </TableCell>
                            <TableCell>{patient.tnsPatientId || ""}</TableCell>
                            <TableCell>{patient.memberId}</TableCell>
                            <TableCell>
                              {patient.lastname}, {patient.firstname}
                            </TableCell>
                            <TableCell>{patient.dob}</TableCell>
                            <TableCell>
                              {[
                                patient.address1,
                                patient.city,
                                patient.state,
                                patient.zip,
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            </TableCell>
                            <TableCell>{patient.glTestRate}</TableCell>
                            <TableCell align='center'>
                              {patient.hasClientAssociation && (
                                <CheckCircle color='success' />
                              )}
                            </TableCell>
                            <TableCell align='center'>
                              {patient.existsInTNS && (
                                <CheckCircle color='success' />
                              )}
                            </TableCell>
                          </TableRow>
                          {patient.existsInTNS && patient.matchMethod && (
                            <TableRow>
                              <TableCell
                                colSpan={9}
                                sx={{
                                  py: 0.5,
                                  px: 2,
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                  bgcolor: "grey.50",
                                }}
                              >
                                <Typography
                                  variant='caption'
                                  sx={{
                                    fontSize: "0.75rem",
                                    fontStyle: "italic",
                                    color:
                                      patient.matchConfidence === "high"
                                        ? "success.main"
                                        : patient.matchConfidence === "medium"
                                          ? "warning.main"
                                          : "warning.dark",
                                  }}
                                >
                                  TNS Match: {patient.matchMethod}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant='contained'
                color={
                  selectedSheet.toLowerCase() === "deactivate"
                    ? "error"
                    : "primary"
                }
                size='large'
                onClick={
                  selectedSheet.toLowerCase() === "deactivate"
                    ? handleDeactivate
                    : handleImport
                }
                disabled={
                  importing ||
                  selectedPatients.size === 0 ||
                  !selectedClient ||
                  (selectedSheet.toLowerCase() !== "deactivate" &&
                    selectedProgram === "none")
                }
              >
                {importing ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {selectedSheet.toLowerCase() === "deactivate"
                      ? "Deactivating..."
                      : "Processing Enrollments..."}
                  </>
                ) : selectedSheet.toLowerCase() === "deactivate" ? (
                  `Deactivate ${selectedPatients.size} Patient(s)`
                ) : (
                  `Process ${selectedPatients.size} Enrollment(s)`
                )}
              </Button>
            </Box>
          </>
        )}

        {/* Step 7: Import Results */}
        {importResults.length > 0 && (
          <Card sx={{ mb: 3, mt: 3 }}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Enrollment Results
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient Name</TableCell>
                      <TableCell>Member ID</TableCell>
                      <TableCell>TNS System</TableCell>
                      <TableCell>Partner System</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResults.map((result, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {result.patient.firstname} {result.patient.lastname}
                        </TableCell>
                        <TableCell>{result.patient.memberId}</TableCell>
                        <TableCell>
                          {result.tnsSuccess ? (
                            <Chip
                              label={
                                result.tnsWasExisting ? "Existing" : "Success"
                              }
                              color='success'
                              size='small'
                            />
                          ) : (
                            <Box>
                              <Chip label='Failed' color='error' size='small' />
                              {result.tnsError && (
                                <Typography
                                  variant='caption'
                                  display='block'
                                  color='error'
                                >
                                  {result.tnsError}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.partnerSuccess ? (
                            <Chip
                              label={
                                result.partnerWasExisting
                                  ? "Existing"
                                  : "Success"
                              }
                              color='success'
                              size='small'
                            />
                          ) : (
                            <Box>
                              <Chip
                                label='Failed'
                                color='warning'
                                size='small'
                              />
                              {result.partnerError && (
                                <Typography
                                  variant='caption'
                                  display='block'
                                  color='error'
                                >
                                  {result.partnerError}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  {importResults.filter((r) => r.tnsSuccess).length} of{" "}
                  {importResults.length} successfully imported to TNS •{" "}
                  {importResults.filter((r) => r.partnerSuccess).length} of{" "}
                  {importResults.filter((r) => !r.patient.existsInTNS).length}{" "}
                  new patients synced to partner system
                </Typography>
              </Box>

              {importResults.length > 0 &&
                importResults.every(
                  (r) => r.tnsSuccess && r.partnerSuccess,
                ) && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant='h6' gutterBottom>
                      Purchase Order Options
                    </Typography>

                    <RadioGroup
                      value={poOption}
                      onChange={(e) => {
                        setPOOption(e.target.value as "new" | "existing");
                        if (e.target.value === "existing") {
                          loadMatchingPOs();
                        }
                      }}
                    >
                      <FormControlLabel
                        value='new'
                        control={<Radio />}
                        label='Generate New Purchase Order'
                      />
                      <FormControlLabel
                        value='existing'
                        control={<Radio />}
                        label='Add to Existing Purchase Order (Building Status)'
                      />
                    </RadioGroup>

                    {poOption === "existing" && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        {loadingPOs ? (
                          <CircularProgress size={24} />
                        ) : matchingPOs.length === 0 ? (
                          <Alert severity='info'>
                            No matching purchase orders found in Building status
                            for this client.
                          </Alert>
                        ) : (
                          <>
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ mb: 1 }}
                            >
                              Select a purchase order to add these patients to:
                            </Typography>
                            <Select
                              value={selectedPO}
                              onChange={(e) =>
                                setSelectedPO(Number(e.target.value))
                              }
                              fullWidth
                              displayEmpty
                            >
                              <MenuItem value={0}>Select a PO...</MenuItem>
                              {matchingPOs.map((po) => (
                                <MenuItem key={po.id} value={po.id}>
                                  {po.poNumber} - {po.clientName}
                                  {po.subClientName &&
                                    ` → ${po.subClientName}`}{" "}
                                  ({po.itemCount} items) - {po.comment}
                                </MenuItem>
                              ))}
                            </Select>
                          </>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant='contained'
                        color='success'
                        size='large'
                        startIcon={<ShoppingCart />}
                        onClick={
                          poOption === "new"
                            ? handleGeneratePO
                            : handleAddToExistingPO
                        }
                        disabled={
                          generatingPO ||
                          (poOption === "existing" &&
                            (loadingPOs || selectedPO === 0))
                        }
                      >
                        {generatingPO ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            {poOption === "new" ? "Generating..." : "Adding..."}
                          </>
                        ) : poOption === "new" ? (
                          "Generate New PO"
                        ) : (
                          "Add to Selected PO"
                        )}
                      </Button>
                    </Box>
                  </Box>
                )}
            </CardContent>
          </Card>
        )}

        {/* Deactivation Results */}
        {deactivationResults.length > 0 && (
          <Card sx={{ mb: 3, mt: 3 }}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Deactivation Results
              </Typography>
              <Alert severity='success' sx={{ mb: 2 }}>
                Successfully deactivated {deactivationResults.length} patient(s)
              </Alert>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient Name</TableCell>
                      <TableCell>Member ID</TableCell>
                      <TableCell>TNS System</TableCell>
                      <TableCell>CCM System</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deactivationResults.map((result, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{result.patientName}</TableCell>
                        <TableCell>{result.memberId}</TableCell>
                        <TableCell>
                          {result.tnsSuccess ? (
                            <Chip
                              label='Deactivated'
                              color='success'
                              size='small'
                            />
                          ) : result.tnsAlreadyDeactivated ? (
                            <Chip
                              label='Already Deactivated'
                              color='warning'
                              size='small'
                            />
                          ) : (
                            <Chip label='Failed' color='error' size='small' />
                          )}
                        </TableCell>
                        <TableCell>
                          {result.ccmSuccess === null ? (
                            <Chip
                              label='No CCM ID'
                              color='default'
                              size='small'
                            />
                          ) : result.ccmSuccess ? (
                            <Chip
                              label='Deactivated'
                              color='success'
                              size='small'
                            />
                          ) : (
                            <Box>
                              <Chip
                                label='Failed'
                                color='warning'
                                size='small'
                              />
                              {result.ccmError && (
                                <Typography
                                  variant='caption'
                                  display='block'
                                  color='error'
                                >
                                  {result.ccmError}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  {deactivationResults.filter((r) => r.tnsSuccess).length} of{" "}
                  {deactivationResults.length} deactivated in TNS •{" "}
                  {
                    deactivationResults.filter((r) => r.ccmSuccess === true)
                      .length
                  }{" "}
                  of{" "}
                  {
                    deactivationResults.filter((r) => r.ccmSuccess !== null)
                      .length
                  }{" "}
                  deactivated in CCM
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Layout>
  );
};

export default PatientImport;
